import React, { useState, useEffect } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';

// Theme context - in real app this would come from context
const getCurrentTheme = () => 'dark'; // Mock theme getter
const setCurrentTheme = (theme: string) => {
  console.log('Theme changed to:', theme);
  // In real app, this would update the theme context
};

const Settings: React.FC = () => {
  const [theme, setThemeState] = useState(getCurrentTheme());
  const [settings, setSettings] = useState({
    notifications: {
      push: true,
      email: true,
      sms: false,
      chama_updates: true,
      financial_alerts: true,
      marketing: false,
      sound_enabled: true,
      vibration_enabled: true,
      volume_level: 80,
    },
    privacy: {
      profile_visibility: 'chama_members',
      transaction_privacy: true,
      location_sharing: false,
    },
    security: {
      biometric_login: false,
      two_factor_auth: false,
      auto_logout: true,
    },
    preferences: {
      language: 'en',
      currency: 'KES',
      date_format: 'dd/mm/yyyy',
    },
  });

  const colors = getThemeColors(theme);

  const handleSettingChange = (category: string, setting: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category as keyof typeof prev],
        [setting]: value,
      },
    }));
  };

  const handleThemeToggle = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setThemeState(newTheme);
    setCurrentTheme(newTheme);
  };

  const handleSaveSettings = async () => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      alert('Settings updated successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to update settings. Please try again.');
    }
  };

  const showProfileVisibilityOptions = () => {
    const options = [
      { label: 'Everyone', value: 'everyone' },
      { label: 'Chama Members Only', value: 'chama_members' },
      { label: 'Private', value: 'private' }
    ];

    const selectedOption = options.find(opt => opt.value === settings.privacy.profile_visibility);
    const selectedIndex = selectedOption ? options.indexOf(selectedOption) : 0;

    // In a real app, this would show a modal or dropdown
    const newValue = options[(selectedIndex + 1) % options.length].value;
    handleSettingChange('privacy', 'profile_visibility', newValue);
  };

  const showLanguageOptions = () => {
    const options = [
      { label: 'English', value: 'en' },
      { label: 'Kiswahili', value: 'sw' },
      { label: 'Kikuyu', value: 'ki' }
    ];

    const selectedOption = options.find(opt => opt.value === settings.preferences.language);
    const selectedIndex = selectedOption ? options.indexOf(selectedOption) : 0;

    const newValue = options[(selectedIndex + 1) % options.length].value;
    handleSettingChange('preferences', 'language', newValue);
  };

  const showCurrencyOptions = () => {
    const options = [
      { label: 'KES (Kenyan Shilling)', value: 'KES' },
      { label: 'USD (US Dollar)', value: 'USD' },
      { label: 'EUR (Euro)', value: 'EUR' }
    ];

    const selectedOption = options.find(opt => opt.value === settings.preferences.currency);
    const selectedIndex = selectedOption ? options.indexOf(selectedOption) : 0;

    const newValue = options[(selectedIndex + 1) % options.length].value;
    handleSettingChange('preferences', 'currency', newValue);
  };

  const showDateFormatOptions = () => {
    const options = [
      { label: 'DD/MM/YYYY', value: 'dd/mm/yyyy' },
      { label: 'MM/DD/YYYY', value: 'mm/dd/yyyy' },
      { label: 'YYYY-MM-DD', value: 'yyyy-mm-dd' }
    ];

    const selectedOption = options.find(opt => opt.value === settings.preferences.date_format);
    const selectedIndex = selectedOption ? options.indexOf(selectedOption) : 0;

    const newValue = options[(selectedIndex + 1) % options.length].value;
    handleSettingChange('preferences', 'date_format', newValue);
  };

  const showVolumeOptions = () => {
    const options = [
      { label: 'Low (25%)', value: 25 },
      { label: 'Medium (50%)', value: 50 },
      { label: 'High (75%)', value: 75 },
      { label: 'Maximum (100%)', value: 100 }
    ];

    const selectedOption = options.find(opt => opt.value === settings.notifications.volume_level);
    const selectedIndex = selectedOption ? options.indexOf(selectedOption) : 0;

    const newValue = options[(selectedIndex + 1) % options.length].value;
    handleSettingChange('notifications', 'volume_level', newValue);
  };

  const handleDeleteAccount = () => {
    if (window.confirm('This action cannot be undone. All your data will be permanently deleted. Are you sure?')) {
      if (window.confirm('Type "DELETE" to confirm account deletion')) {
        alert('Account deletion functionality would be implemented here');
      }
    }
  };

  const navigateToProfile = () => {
    window.location.href = '/dashboard/profile';
  };

  const navigateToSecuritySettings = () => {
    window.location.href = '/dashboard/settings/security';
  };

  const navigateToPaymentMethods = () => {
    window.location.href = '/dashboard/settings/payment-methods';
  };

  const navigateToNotifications = () => {
    window.location.href = '/dashboard/notifications';
  };

  const navigateToHelpCenter = () => {
    window.location.href = '/dashboard/settings/help-center';
  };

  const navigateToNotificationTone = () => {
    window.location.href = '/dashboard/settings/notification-tone';
  };

  const navigateToContactSupport = () => {
    window.location.href = '/dashboard/settings/contact-support';
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: spacing.lg,
    },
    section: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      marginBottom: spacing.xl,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('lg'),
    },
    sectionTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.lg,
      paddingBottom: spacing.md,
      borderBottom: `2px solid ${colors.primary}`,
    },
    settingItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: spacing.xl,
      borderBottom: `1px solid ${colors.border}`,
      marginBottom: spacing.lg,
      borderRadius: borderRadius.lg,
      backgroundColor: colors.surface,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('md'),
      transition: 'all 0.2s ease',
    },
    settingInfo: {
      flex: 1,
    },
    settingTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    settingDescription: {
      fontSize: typography.fontSize.lg,
      color: colors.textSecondary,
      lineHeight: 1.5,
    },
    settingValue: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      padding: `${spacing.md} ${spacing.lg}`,
      borderRadius: borderRadius.md,
      backgroundColor: colors.backgroundSecondary,
      minWidth: 140,
      justifyContent: 'center',
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('sm'),
      transition: 'all 0.2s ease',
    },
    settingValueText: {
      fontSize: typography.fontSize.lg,
      color: colors.text,
      fontWeight: typography.fontWeight.semibold,
    },
    themeContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: spacing.lg,
    },
    themeOption: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: spacing.xl,
      borderRadius: borderRadius.lg,
      border: `2px solid ${colors.border}`,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backgroundColor: colors.surface,
      ...getShadowStyle('md'),
    },
    themeOptionSelected: {
      backgroundColor: colors.primary + '15',
      borderColor: colors.primary,
      transform: 'scale(1.02)',
      boxShadow: `0 4px 12px ${colors.primary}30`,
    },
    themeIcon: {
      fontSize: 48,
      marginBottom: spacing.md,
    },
    themeText: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
    },
    menuItem: {
      display: 'flex',
      alignItems: 'center',
      padding: spacing.lg,
      borderBottom: `1px solid ${colors.border}`,
      cursor: 'pointer',
      borderRadius: borderRadius.md,
      backgroundColor: colors.surface,
      marginBottom: spacing.sm,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('sm'),
      transition: 'all 0.2s ease',
    },
    menuItemHover: {
      backgroundColor: colors.backgroundSecondary,
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 8px ${colors.shadow}`,
    },
    menuIcon: {
      width: 56,
      height: 56,
      borderRadius: 28,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: spacing.lg,
      fontSize: 24,
      backgroundColor: colors.primary + '15',
      color: colors.primary,
    },
    menuContent: {
      flex: 1,
    },
    menuTitle: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
      marginBottom: spacing.xs,
    },
    menuSubtitle: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      lineHeight: 1.4,
    },
    menuArrow: {
      color: colors.primary,
      fontSize: 20,
      fontWeight: 'bold',
    },
    button: {
      padding: `${spacing.lg} ${spacing.xl}`,
      borderRadius: borderRadius.lg,
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      cursor: 'pointer',
      border: 'none',
      width: '100%',
      marginBottom: spacing.lg,
      ...getShadowStyle('md'),
      transition: 'all 0.3s ease',
    },
    primaryButton: {
      backgroundColor: colors.primary,
      color: colors.white,
    },
    primaryButtonHover: {
      backgroundColor: colors.primary,
      transform: 'translateY(-2px)',
      boxShadow: `0 6px 20px ${colors.primary}40`,
    },
    dangerButton: {
      backgroundColor: colors.error,
      color: colors.white,
    },
    dangerButtonHover: {
      backgroundColor: colors.error,
      transform: 'translateY(-2px)',
      boxShadow: `0 6px 20px ${colors.error}40`,
    },
    appInfo: {
      textAlign: 'center',
      padding: spacing.xxxl,
      color: colors.textSecondary,
      fontSize: typography.fontSize.base,
      backgroundColor: colors.backgroundSecondary,
      borderRadius: borderRadius.xl,
      marginTop: spacing.xl,
      border: `1px solid ${colors.border}`,
    },
    appVersion: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.primary,
      marginBottom: spacing.sm,
    },
    appBuild: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
    },
  };

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Settings"
        subtitle="Manage your account and preferences"
        showBackButton={true}
        showHomeButton={true}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Theme Selector */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Appearance</h2>
            <div style={styles.themeContainer}>
              <div
                style={{
                  ...styles.themeOption,
                  ...(theme === 'light' ? styles.themeOptionSelected : {}),
                }}
                onClick={() => setThemeState('light')}
              >
                <div style={styles.themeIcon}>☀️</div>
                <span style={styles.themeText}>Light</span>
              </div>
              <div
                style={{
                  ...styles.themeOption,
                  ...(theme === 'dark' ? styles.themeOptionSelected : {}),
                }}
                onClick={() => setThemeState('dark')}
              >
                <div style={styles.themeIcon}>🌙</div>
                <span style={styles.themeText}>Dark</span>
              </div>
            </div>
          </div>

          {/* Notification Settings */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Notifications</h2>

            <div style={styles.settingItem}>
              <div style={styles.settingInfo}>
                <div style={styles.settingTitle}>Push Notifications</div>
                <div style={styles.settingDescription}>Receive notifications on your device</div>
              </div>
              <input
                type="checkbox"
                checked={settings.notifications.push}
                onChange={(e) => handleSettingChange('notifications', 'push', e.target.checked)}
              />
            </div>

            <div style={styles.settingItem}>
              <div style={styles.settingInfo}>
                <div style={styles.settingTitle}>Email Notifications</div>
                <div style={styles.settingDescription}>Receive notifications via email</div>
              </div>
              <input
                type="checkbox"
                checked={settings.notifications.email}
                onChange={(e) => handleSettingChange('notifications', 'email', e.target.checked)}
              />
            </div>

            <div style={styles.settingItem}>
              <div style={styles.settingInfo}>
                <div style={styles.settingTitle}>Chama Updates</div>
                <div style={styles.settingDescription}>Notifications about chama activities</div>
              </div>
              <input
                type="checkbox"
                checked={settings.notifications.chama_updates}
                onChange={(e) => handleSettingChange('notifications', 'chama_updates', e.target.checked)}
              />
            </div>

            <div style={styles.settingItem}>
              <div style={styles.settingInfo}>
                <div style={styles.settingTitle}>Financial Alerts</div>
                <div style={styles.settingDescription}>Notifications about transactions and balances</div>
              </div>
              <input
                type="checkbox"
                checked={settings.notifications.financial_alerts}
                onChange={(e) => handleSettingChange('notifications', 'financial_alerts', e.target.checked)}
              />
            </div>

            {settings.notifications.push && (
              <>
                <div style={styles.settingItem}>
                  <div style={styles.settingInfo}>
                    <div style={styles.settingTitle}>Sound Enabled</div>
                    <div style={styles.settingDescription}>Play sound for notifications</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.notifications.sound_enabled}
                    onChange={(e) => handleSettingChange('notifications', 'sound_enabled', e.target.checked)}
                  />
                </div>

                <div style={styles.settingItem}>
                  <div style={styles.settingInfo}>
                    <div style={styles.settingTitle}>Vibration Enabled</div>
                    <div style={styles.settingDescription}>Vibrate for notifications</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.notifications.vibration_enabled}
                    onChange={(e) => handleSettingChange('notifications', 'vibration_enabled', e.target.checked)}
                  />
                </div>

                {settings.notifications.sound_enabled && (
                  <div style={styles.settingItem}>
                    <div style={styles.settingInfo}>
                      <div style={styles.settingTitle}>Volume Level</div>
                      <div style={styles.settingDescription}>{settings.notifications.volume_level}%</div>
                    </div>
                    <div style={styles.settingValue} onClick={showVolumeOptions}>
                      <span style={styles.settingValueText}>
                        {settings.notifications.volume_level}%
                      </span>
                      <span>▶️</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Privacy Settings */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Privacy</h2>

            <div style={styles.settingItem}>
              <div style={styles.settingInfo}>
                <div style={styles.settingTitle}>Profile Visibility</div>
                <div style={styles.settingDescription}>Who can see your profile information</div>
              </div>
              <div style={styles.settingValue} onClick={showProfileVisibilityOptions}>
                <span style={styles.settingValueText}>
                  {settings.privacy.profile_visibility === 'everyone' ? 'Everyone' :
                   settings.privacy.profile_visibility === 'chama_members' ? 'Chama Members' :
                   'Private'}
                </span>
                <span>▶️</span>
              </div>
            </div>

            <div style={styles.settingItem}>
              <div style={styles.settingInfo}>
                <div style={styles.settingTitle}>Transaction Privacy</div>
                <div style={styles.settingDescription}>Hide transaction details from other members</div>
              </div>
              <input
                type="checkbox"
                checked={settings.privacy.transaction_privacy}
                onChange={(e) => handleSettingChange('privacy', 'transaction_privacy', e.target.checked)}
              />
            </div>

            <div style={styles.settingItem}>
              <div style={styles.settingInfo}>
                <div style={styles.settingTitle}>Location Sharing</div>
                <div style={styles.settingDescription}>Share your location with chama members</div>
              </div>
              <input
                type="checkbox"
                checked={settings.privacy.location_sharing}
                onChange={(e) => handleSettingChange('privacy', 'location_sharing', e.target.checked)}
              />
            </div>
          </div>

          {/* Security Settings */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Security</h2>

            <div style={styles.settingItem}>
              <div style={styles.settingInfo}>
                <div style={styles.settingTitle}>Biometric Login</div>
                <div style={styles.settingDescription}>Use fingerprint or face recognition</div>
              </div>
              <input
                type="checkbox"
                checked={settings.security.biometric_login}
                onChange={(e) => handleSettingChange('security', 'biometric_login', e.target.checked)}
              />
            </div>

            <div style={styles.settingItem}>
              <div style={styles.settingInfo}>
                <div style={styles.settingTitle}>Two-Factor Authentication</div>
                <div style={styles.settingDescription}>Add an extra layer of security</div>
              </div>
              <input
                type="checkbox"
                checked={settings.security.two_factor_auth}
                onChange={(e) => handleSettingChange('security', 'two_factor_auth', e.target.checked)}
              />
            </div>

            <div style={styles.settingItem}>
              <div style={styles.settingInfo}>
                <div style={styles.settingTitle}>Auto Logout</div>
                <div style={styles.settingDescription}>Automatically logout after inactivity</div>
              </div>
              <input
                type="checkbox"
                checked={settings.security.auto_logout}
                onChange={(e) => handleSettingChange('security', 'auto_logout', e.target.checked)}
              />
            </div>
          </div>

          {/* Preferences Settings */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Preferences</h2>

            <div style={styles.settingItem}>
              <div style={styles.settingInfo}>
                <div style={styles.settingTitle}>Language</div>
                <div style={styles.settingDescription}>Choose your preferred language</div>
              </div>
              <div style={styles.settingValue} onClick={showLanguageOptions}>
                <span style={styles.settingValueText}>
                  {settings.preferences.language === 'en' ? 'English' :
                   settings.preferences.language === 'sw' ? 'Kiswahili' :
                   'Kikuyu'}
                </span>
                <span>▶️</span>
              </div>
            </div>

            <div style={styles.settingItem}>
              <div style={styles.settingInfo}>
                <div style={styles.settingTitle}>Currency</div>
                <div style={styles.settingDescription}>Default currency for transactions</div>
              </div>
              <div style={styles.settingValue} onClick={showCurrencyOptions}>
                <span style={styles.settingValueText}>
                  {settings.preferences.currency}
                </span>
                <span>▶️</span>
              </div>
            </div>

            <div style={styles.settingItem}>
              <div style={styles.settingInfo}>
                <div style={styles.settingTitle}>Date Format</div>
                <div style={styles.settingDescription}>How dates are displayed</div>
              </div>
              <div style={styles.settingValue} onClick={showDateFormatOptions}>
                <span style={styles.settingValueText}>
                  {settings.preferences.date_format}
                </span>
                <span>▶️</span>
              </div>
            </div>
          </div>

          {/* Account Menu */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Account</h2>

            <div style={styles.menuItem} onClick={navigateToProfile}>
              <div style={{ ...styles.menuIcon, backgroundColor: colors.primary + '20' }}>
                👤
              </div>
              <div style={styles.menuContent}>
                <div style={styles.menuTitle}>Profile</div>
                <div style={styles.menuSubtitle}>Manage your personal information</div>
              </div>
              <span style={styles.menuArrow}>▶️</span>
            </div>

            <div style={styles.menuItem} onClick={navigateToSecuritySettings}>
              <div style={{ ...styles.menuIcon, backgroundColor: colors.info + '20' }}>
                🛡️
              </div>
              <div style={styles.menuContent}>
                <div style={styles.menuTitle}>Security Settings</div>
                <div style={styles.menuSubtitle}>Password, PIN, and security options</div>
              </div>
              <span style={styles.menuArrow}>▶️</span>
            </div>

            <div style={styles.menuItem} onClick={navigateToPaymentMethods}>
              <div style={{ ...styles.menuIcon, backgroundColor: colors.secondary + '20' }}>
                💳
              </div>
              <div style={styles.menuContent}>
                <div style={styles.menuTitle}>Payment Methods</div>
                <div style={styles.menuSubtitle}>Manage your payment options</div>
              </div>
              <span style={styles.menuArrow}>▶️</span>
            </div>

            <div style={styles.menuItem} onClick={navigateToNotificationTone}>
              <div style={{ ...styles.menuIcon, backgroundColor: colors.secondary + '20' }}>
                🔔
              </div>
              <div style={styles.menuContent}>
                <div style={styles.menuTitle}>Notification Tones</div>
                <div style={styles.menuSubtitle}>Choose your notification sound</div>
              </div>
              <span style={styles.menuArrow}>▶️</span>
            </div>
          </div>

          {/* Support Menu */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Support</h2>

            <div style={styles.menuItem} onClick={navigateToHelpCenter}>
              <div style={{ ...styles.menuIcon, backgroundColor: colors.warning + '20' }}>
                ❓
              </div>
              <div style={styles.menuContent}>
                <div style={styles.menuTitle}>Help Center</div>
                <div style={styles.menuSubtitle}>FAQs and support articles</div>
              </div>
              <span style={styles.menuArrow}>▶️</span>
            </div>

            <div style={styles.menuItem} onClick={navigateToContactSupport}>
              <div style={{ ...styles.menuIcon, backgroundColor: colors.info + '20' }}>
                💬
              </div>
              <div style={styles.menuContent}>
                <div style={styles.menuTitle}>Contact Support</div>
                <div style={styles.menuSubtitle}>Get help from our team</div>
              </div>
              <span style={styles.menuArrow}>▶️</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={styles.section}>
            <button
              style={{ ...styles.button, ...styles.primaryButton }}
              onClick={handleSaveSettings}
            >
              Save Settings
            </button>

            <button
              style={{ ...styles.button, ...styles.dangerButton }}
              onClick={handleDeleteAccount}
            >
              Delete Account
            </button>
          </div>

          {/* App Info */}
          <div style={styles.appInfo}>
            <div style={styles.appVersion}>VaultKe v1.0.0</div>
            <div style={styles.appBuild}>Build 2024.1.0</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;